'use client';

import Image from 'next/image';
import { ReactNode } from 'react';
import { useTranslations } from 'next-intl';
import closeImg from '@/features/notifications/images/close.png';
import arrowImg from '@/features/notifications/images/banner-arrow.png';
import successImg from '@/features/notifications/images/finished.png';
import failedImg from '@/features/notifications/images/failed.png';
import loadingImg from '@/features/notifications/images/confirming.png';
import infoImg from '@/features/notifications/images/info.png';
import { useGetStaticResource, getCurrencyIcon } from '@/services/static-resource';
import { NotificationEventType } from '../constant/index';
import { useBalanceFormatter } from '@/hooks/use-balance-formatter';

interface NotifyBannerContainerProps {
  /**
   * 左侧 slot 内容
   */
  children: ReactNode;
  /**
   * 是否可见
   */
  isVisible: boolean;
  /**
   * 是否显示关闭按钮
   */
  showCloseButton?: boolean;
  /**
   * 横幅点击事件
   */
  onBannerClick: () => void;
  /**
   * 关闭按钮点击事件
   */
  onCloseAction: () => void;
}

/**
 * 通知横幅容器组件
 * 固定宽度360px，水平居中，带渐变背景和关闭按钮
 */
export default function NotifyBannerContainer({
  children,
  isVisible,
  showCloseButton = false,
  onBannerClick,
  onCloseAction,
}: NotifyBannerContainerProps) {

  const handleClose = (e: React.MouseEvent) => {
    e?.stopPropagation();
    onCloseAction();
  };

  return (
    <div
      className={`fixed top-[80px] left-1/2 -translate-x-1/2 flex items-center justify-between w-[360px] h-[55px] px-[15px] pr-[0px] z-[9999] bg-[linear-gradient(90deg,#2A3546,#181D29)] rounded-[8px] transition-all duration-300 ease-out ${isVisible
        ? 'opacity-100 scale-100 translate-y-0'
        : 'opacity-0 scale-95 translate-y-[-8px]'
        }`}
      onClick={onBannerClick}
    >
      {/* 左侧 slot 内容 */}
      <div className="flex items-center h-full w-[90%]">
        <div className='w-full truncate'>{children}</div>
      </div>

      {/* 右侧关闭按钮 */}
      {showCloseButton && (
        <div className='flex items-center justify-center flex-shrink-0 w-[34px] h-[34px] mr-[3px] pressable' onClick={handleClose}>
          <Image

            src={closeImg}
            alt=""
            width={10}
            height={10}
            className="w-[10px] h-[10px] cursor-pointer flex-shrink-0"
          />
        </div>
      )}

      {/* 右侧跳转按钮 */}
      {!showCloseButton && (
        <div className='flex items-center justify-center flex-shrink-0 transform rotate-[-90deg] mr-[15px]'>
          <Image

            src={arrowImg}
            alt=""
            width={10}
            height={6}
            className="w-[10px] h-[6px] cursor-pointer flex-shrink-0"
          />
        </div>
      )
      }
    </div >
  );
}

interface TransactionNotifyProps {
  /**
   * 货币代码
   */
  currency: string;
  /**
   * 金额
   */
  amount: string;
  /**
   * 事件类型: 1=平台消息, 2=充值, 3=提现, 4=代理佣金, 5=VIP相关, 6=Rummy相关
   */
  eventType: NotificationEventType;
  /**
   * 事件状态: 1=成功, 2=失败, 3=处理中
   */
  eventStatus: number;
}

/**
 * 交易通知内容组件
 * 显示货币图标、状态图标和相应文案
 */
export function TransactionNotify({
  currency,
  amount,
  eventType,
  eventStatus,
}: TransactionNotifyProps) {
  const t = useTranslations('notifications');
  const { data: staticResource } = useGetStaticResource();
  const { formatCurrencyDetail } = useBalanceFormatter();

  const amountFormatted = formatCurrencyDetail(amount, {
    applyKMBT: true,
    currency: currency,
  });

  // 根据 eventStatus 获取状态图标
  const getStatusIcon = () => {
    switch (eventStatus) {
      case 1: // 成功
        return successImg;
      case 2: // 失败
        return failedImg;
      case 3: // 处理中
        return loadingImg;
      default:
        return loadingImg;
    }
  };

  // 获取货币图标
  const currencyIcon = staticResource ? getCurrencyIcon(currency, staticResource) : '';

  // 获取文案
  const getText = () => {
    if (eventType === NotificationEventType.Recharge) {
      // 充值
      switch (eventStatus) {
        case 1: // 成功
          return t('depositSuccess', { amount: amountFormatted, Currency: currency });
        case 2: // 失败
          return t('depositFail', { amount: amountFormatted, Currency: currency });
        case 3: // 处理中
          return t('depositProcessing', { amount: amountFormatted, Currency: currency });
        default:
          return '';
      }
    } else if (eventType === NotificationEventType.Withdraw) {
      // 提现
      switch (eventStatus) {
        case 1: // 成功
          return t('withdrawSuccess', { amount: amountFormatted, Currency: currency });
        case 2: // 失败
          return t('withdrawFail', { amount: amountFormatted, Currency: currency });
        case 3: // 处理中
          return t('withdrawProcessing', { amount: amountFormatted, Currency: currency });
        default:
          return '';
      }
    }
    return '';
  };

  return (
    <div className="flex items-center w-full">
      {/* 货币图标容器 */}
      <div className="relative w-[26px] h-[26px] mr-[10px] flex items-center">
        {/* 货币图标 */}
        {currencyIcon && (
          <Image
            src={currencyIcon}
            alt=""
            width={26}
            height={26}
            className="w-full h-auto"
          />
        )}
        {/* 状态图标 */}
        <Image
          src={getStatusIcon()}
          alt=""
          width={13}
          height={13}
          className="absolute w-[13px] h-[13px] top-[13px] left-[18px]"
        />
      </div>

      {/* 文案 */}
      <div className="font-medium text-xs text-white w-full truncate">
        {getText()}
      </div>
    </div>
  );
}

interface GeneralNotifyProps {
  /**
   * 事件类型: 1=平台消息, 2=充值, 3=提现, 4=代理佣金, 5=VIP相关, 6=Rummy相关, 7=累充
   */
  eventType: NotificationEventType;
  /**
   * 事件子类型（VIP相关）
   */
  eventSubType?: number;
  /**
   * 金额（代理佣金使用）
   */
  amount: string;
  /**
   * 通知内容（平台消息使用）
   */
  content?: string;
  /**
   * 消息数量
   */
  msgCount: number;
  /**
   * 消息额外参数(json字符串)
   */
  jumpParams: string;
  /**
   * 币种
   */
  currency: string;
}

/**
 * 通用通知内容组件
 * 显示信息图标和相应文案
 */
export function GeneralNotify({
  eventType,
  eventSubType,
  amount,
  content,
  msgCount,
  jumpParams,
  currency
}: GeneralNotifyProps) {
  const t = useTranslations('notifications');
  const { formatCurrencyDetail } = useBalanceFormatter();
  const amountFormatted = formatCurrencyDetail(amount, {
    applyKMBT: true,
    currency: currency,
  });

  // 获取文案
  const getText = () => {
    // 平台消息
    if (eventType === NotificationEventType.Platform) {
      return content || '';
    }

    // 代理佣金
    if (eventType === NotificationEventType.Proxy) {
      const proxyParams = jumpParams ? JSON.parse(jumpParams) : {};
      switch (eventSubType) {
        case 1:
          // 代理派奖
          if (amount) {
            return t('commissionArrivedShort', { currency: currency, amount: amountFormatted });
          }
          return '';
        case 2:
          // 代理绑定-受邀
          return t('bindSuccessSingle', { username: proxyParams.username, 0: proxyParams.uid });
        case 3:
          // 代理绑定-邀请
          return t('bindSuccessSingle', { username: proxyParams.username, 0: proxyParams.uid });
        case 4:
          // 实时奖励消息
          return t('agentRewardArrived', { currency: currency, amount: amountFormatted });
        case 5:
          // 临时被邀请奖励赠送
          return content || '';
        default:
          return '';
      }
    }

    // VIP相关
    if (eventType === NotificationEventType.VIP) {
      switch (eventSubType) {
        case 1:
          return t('welcomeVipClub');
        case 2:
          return t('levelUpTitle');
        case 3:
          return t('rewardExpiredTitle');
        default:
          return '';
      }
    }

    // Rummy相关
    if (eventType === NotificationEventType.Rummy) {
      return content;
    }

    // 补偿相关
    if (eventType === NotificationEventType.Compensation) {
      return content;
    }

    // 累充转盘相关
    if (eventType === NotificationEventType.CumulativeDeposit) {
      switch (eventSubType) {
        case 1:
          return t('cumulativeRechargeEventOpen');
        case 2:
          return t('drawAttemptsExpiring');
        default:
          return '';
      }
    }

    // 聚合消息
    if (eventType === NotificationEventType.AggregationMsg) {
      return t('youHaveNMsgs', { number: msgCount });
    }

    return '';
  };

  const text = getText();

  return (
    <div className="flex items-center w-full">
      {/* 信息图标 */}
      <Image
        src={infoImg}
        alt=""
        width={13}
        height={13}
        className="w-[13px] h-[13px] mr-[9px]"
      />

      {/* 内容 */}
      <div className="font-medium text-xs text-white w-full truncate">
        {text}
      </div>
    </div>
  );
}
